import { z } from 'zod';
/**
 * Versioned consent workflow contracts.
 *
 * `field.ts` still carries the current tablet-only submission consent shape.
 * These schemas describe the next first-class consent workflow: remote
 * pre-sign links, tablet fallback, immutable PDFs, and QA evidence.
 */
export const ConsentTemplateIdSchema = z.enum(['t45-pv-only', 't45-pv-battery']);
export const ConsentDeliveryChannelSchema = z.enum(['email', 'sms', 'tablet']);
export const ConsentRequestStatusSchema = z.enum([
    'draft',
    'sent',
    'viewed',
    'signed',
    'expired',
    'revoked',
]);
export const ConsentRecipientSchema = z.object({
    name: z.string().trim().min(1).optional(),
    email: z.string().trim().email().optional(),
    phone: z.string().trim().min(6).optional(),
});
export const ConsentBookingSnapshotSchema = z.record(z.string(), z.unknown());
export const ConsentRequestSchema = z
    .object({
    requestId: z.string().uuid(),
    caseId: z.string().min(1),
    templateId: ConsentTemplateIdSchema,
    templateVersion: z.string().min(1),
    status: ConsentRequestStatusSchema,
    channel: ConsentDeliveryChannelSchema,
    recipient: ConsentRecipientSchema.optional(),
    /** Hash of the opaque remote token. The raw token is never stored. */
    tokenHash: z.string().min(32).optional(),
    expiresAt: z.string(),
    createdByUserId: z.string().min(1),
    createdAt: z.string(),
    updatedAt: z.string(),
    revokedAt: z.string().optional(),
    signedRecordId: z.string().uuid().optional(),
})
    .superRefine((data, ctx) => {
    if (data.channel === 'email' && !data.recipient?.email) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'email recipient is required for email consent requests',
            path: ['recipient', 'email'],
        });
    }
    if (data.channel === 'sms' && !data.recipient?.phone) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'phone recipient is required for SMS consent requests',
            path: ['recipient', 'phone'],
        });
    }
    if ((data.channel === 'email' || data.channel === 'sms') && !data.tokenHash) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'tokenHash is required for remote consent requests',
            path: ['tokenHash'],
        });
    }
    if (data.status === 'signed' && !data.signedRecordId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'signedRecordId is required when request status is signed',
            path: ['signedRecordId'],
        });
    }
    if (data.status === 'revoked' && !data.revokedAt) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'revokedAt is required when request status is revoked',
            path: ['revokedAt'],
        });
    }
});
export const ConsentCriticalEditFieldSchema = z.enum([
    'siteAddress',
    'pvdCode',
    'btyCode',
    'inspectionType',
]);
export const SignedConsentRecordSchema = z.object({
    recordId: z.string().uuid(),
    requestId: z.string().uuid().optional(),
    caseId: z.string().min(1),
    templateId: ConsentTemplateIdSchema,
    templateVersion: z.string().min(1),
    signedAt: z.string(),
    signerName: z.string().trim().min(1),
    signerChannel: ConsentDeliveryChannelSchema,
    bookingSnapshot: ConsentBookingSnapshotSchema,
    homeownerEdits: z.record(z.string(), z.unknown()).default({}),
    criticalEditFields: z.array(ConsentCriticalEditFieldSchema).default([]),
    /** The consent form must use Jason's fixed inspector details. */
    inspectorDetails: z.literal('jason-satchell-fixed'),
    signatureSha256: z.string().min(64).max(64),
    pdfArtefactKey: z.string().min(1),
    auditEventIds: z.array(z.string().uuid()).default([]),
    remoteEvidence: z
        .object({
        ipAddress: z.string().optional(),
        userAgent: z.string().optional(),
        viewedAt: z.string().optional(),
    })
        .optional(),
    deviceId: z.string().min(1).optional(),
});
export const ConsentArtefactSchema = z.object({
    artefactId: z.string().uuid(),
    caseId: z.string().min(1),
    recordId: z.string().uuid(),
    kind: z.enum(['signed-consent-pdf']),
    s3Key: z.string().min(1),
    sha256: z.string().min(64).max(64),
    createdAt: z.string(),
});
export function isTerminalConsentRequestStatus(status) {
    return status === 'signed' || status === 'expired' || status === 'revoked';
}
export function consentChannelRequiresRemoteRecipient(channel) {
    return channel === 'email' || channel === 'sms';
}
//# sourceMappingURL=consent.js.map